/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

/**
 * IndexedDB storage utilities for Hivemind projects
 * Projects are stored in IndexedDB with optional file system sync
 */

import { PERSONA_LIBRARY, generateSystemPrompt } from '@/core/personas'

const DB_NAME = 'hivemind-db'
const DB_VERSION = 2
const PROJECTS_STORE = 'projects'
const DIRECTORY_HANDLES_STORE = 'directoryHandles'
const WORKSPACE_KEY = 'workspace'

export interface ChatMessage {
  id: string
  role: 'system' | 'user' | 'assistant' | 'tool'
  content: any // string | MessageContent
  timestamp: number
  attachedFiles?: Array<{ path: string; content: string }> // Files attached to this message
}

export interface PeerResponse {
  personaId: string
  personaName: string
  content: string
  timestamp: number
  tokens?: number
  cost?: number
}

export interface JudgeResult {
  score: number
  reasoning: string
  timestamp: number
}

export interface PeerConfig {
  id: string
  personaId: string // ID from persona library or 'custom'
  personaName: string
  model: string
  systemPrompt: string // Editable system prompt
  enabled: boolean
  collapsed: boolean
  color: string // For UI theming
  icon: string // Icon identifier
  preserveContext?: boolean // Whether to maintain conversation history
}

export interface Project {
  id: string
  name: string
  description?: string
  createdAt: number
  updatedAt: number
  files: Record<string, string> // filename -> content
  fileSystemPath?: string // Optional: path to project folder on disk
  github?: {
    owner: string
    repo: string
    branch: string
    lastCommitSha?: string
    lastSyncedAt?: number
    url: string // Full GitHub URL
    fullName?: string
  }
  storageType?: 'local' | 'cloud' // Where the project is stored
  userId?: string // Owner ID for cloud projects
  sharedWith?: string[] // List of emails this project is shared with
  isPublic?: boolean // Whether the project is public (viewable by anyone with link)
  settings?: {
    apiKeys?: Record<string, string>
    defaultProvider?: string
    models?: {
      gemini?: string
      claude?: string
      grok?: string
      judge?: string
      orchestrator?: string
    }
    peers?: PeerConfig[] // Dynamic peer configurations
    // Model tier preferences
    modelTierPreferences?: {
      preferredTier?: 'ultra-cheap' | 'budget' | 'premium-cheap' | 'premium-expensive'
      preferredOrigin?: 'us' | 'eu' | 'china' | 'international' | 'any'
      useTierForPeers?: boolean // Auto-select peer models based on tier
      useTierForJudge?: boolean // Auto-select judge model based on tier
      useTierForOrchestrator?: boolean // Auto-select orchestrator model based on tier
    }
  }
  // Thread data - orchestrator chat and peer responses
  thread?: {
    messages: ChatMessage[] // Orchestrator conversation history
    peerResponses: Record<string, PeerResponse | null> // Dynamic peer responses by peer ID
    judgeResult: JudgeResult | null
    // Conversation history for each peer (for context in future queries)
    peerHistory?: Record<string, ChatMessage[]> // Dynamic peer history by peer ID
    // Judge conversation history
    judgeHistory?: ChatMessage[]
    // Judge settings
    judgeSettings?: {
      autoSendToOrchestrator: boolean
      scoreThreshold: number // e.g., 0.8 for approval
      userNotes?: string // User's notes/edits to judgment
    }
  }
}

let db: IDBDatabase | null = null

/**
 * Initialize IndexedDB
 */
export async function initDB(): Promise<IDBDatabase> {
  if (db) return db

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onerror = () => reject(request.error)
    request.onsuccess = () => {
      db = request.result
      resolve(db)
    }

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result

      // Create projects store
      if (!db.objectStoreNames.contains(PROJECTS_STORE)) {
        const store = db.createObjectStore(PROJECTS_STORE, { keyPath: 'id' })
        store.createIndex('updatedAt', 'updatedAt', { unique: false })
      }

      // Create directory handles store (for File System Access API)
      if (!db.objectStoreNames.contains(DIRECTORY_HANDLES_STORE)) {
        db.createObjectStore(DIRECTORY_HANDLES_STORE)
      }
    }
  })
}

/**
 * Save a project to IndexedDB
 */
export async function saveProject(project: Project): Promise<void> {
  const database = await initDB()

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([PROJECTS_STORE], 'readwrite')
    const store = transaction.objectStore(PROJECTS_STORE)

    project.updatedAt = Date.now()
    const request = store.put(project)

    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
  })
}

/**
 * Load a project from IndexedDB
 */
export async function loadProject(id: string): Promise<Project | null> {
  const database = await initDB()

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([PROJECTS_STORE], 'readonly')
    const store = transaction.objectStore(PROJECTS_STORE)
    const request = store.get(id)

    request.onsuccess = () => resolve(request.result || null)
    request.onerror = () => reject(request.error)
  })
}

/**
 * List all projects
 */
/**
 * List all projects for a specific user
 * Migrates legacy (no-userId) projects to the first user who sees them
 */
export async function listProjects(userId?: string): Promise<Project[]> {
  const database = await initDB()

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([PROJECTS_STORE], 'readwrite')
    const store = transaction.objectStore(PROJECTS_STORE)
    const request = store.getAll()

    request.onsuccess = () => {
      const allProjects: Project[] = request.result || []
      let needsSave = false

      // Process projects:
      // 1. Migrate old projects (no updatedAt)
      // 2. Claim orphan projects (no userId) if we have a userId
      const processedProjects = allProjects.map(project => {
        let changed = false

        // Migration 1: Add updatedAt
        if (!project.updatedAt) {
          project.updatedAt = project.createdAt || Date.now()
          changed = true
        }

        // Migration 2: Claim orphan projects
        if (userId && !project.userId) {
          project.userId = userId
          changed = true
        }

        if (changed) needsSave = true
        return project
      })

      // Save any modified projects back to DB
      if (needsSave) {
        processedProjects.forEach(project => {
          store.put(project)
        })
      }

      // Filter by userId if provided
      // If no userId provided (e.g. pre-login check), return nothing or public? 
      // For now, we only return projects matching userId
      const userProjects = userId
        ? processedProjects.filter(p => p.userId === userId)
        : []

      // Sort by updatedAt (most recent first)
      userProjects.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))

      resolve(userProjects)
    }

    request.onerror = () => reject(request.error)
  })
}

/**
 * Delete a project
 */
export async function deleteProject(id: string): Promise<void> {
  const database = await initDB()

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([PROJECTS_STORE], 'readwrite')
    const store = transaction.objectStore(PROJECTS_STORE)
    const request = store.delete(id)

    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
  })
}

/**
 * Create a new project with default structure
 */
export function createNewProject(name: string, userId?: string): Project {
  const now = Date.now()

  // Get default personas from the library
  const technicalReviewer = PERSONA_LIBRARY.find(p => p.id === 'Technical_Reviewer')
  const contentEditor = PERSONA_LIBRARY.find(p => p.id === 'Content_Editor')
  const innovationAdvisor = PERSONA_LIBRARY.find(p => p.id === 'Innovation_Advisor')

  // Fallback if personas not found (should never happen)
  if (!technicalReviewer || !contentEditor || !innovationAdvisor) {
    throw new Error('Core personas not found in persona library')
  }

  return {
    id: `project-${now}`,
    name,
    createdAt: now,
    updatedAt: now,
    settings: {
      models: {
        gemini: 'google/gemini-2.5-pro',
        claude: 'anthropic/claude-sonnet-4.5',
        grok: 'x-ai/grok-4',
        judge: 'anthropic/claude-sonnet-4.5',
      },
      peers: [
        {
          id: 'peer-1',
          personaId: technicalReviewer.id,
          personaName: technicalReviewer.name,
          model: technicalReviewer.recommendedModels[0] || 'google/gemini-2.5-pro',
          systemPrompt: generateSystemPrompt(technicalReviewer),
          enabled: true,
          collapsed: false,
          color: 'text-purple-400',
          icon: 'gemini',
        },
        {
          id: 'peer-2',
          personaId: contentEditor.id,
          personaName: contentEditor.name,
          model: contentEditor.recommendedModels[0] || 'anthropic/claude-sonnet-4.5',
          systemPrompt: generateSystemPrompt(contentEditor),
          enabled: true,
          collapsed: false,
          color: 'text-orange-400',
          icon: 'claude',
        },
        {
          id: 'peer-3',
          personaId: innovationAdvisor.id,
          personaName: innovationAdvisor.name,
          model: innovationAdvisor.recommendedModels[0] || 'x-ai/grok-4',
          systemPrompt: generateSystemPrompt(innovationAdvisor),
          enabled: true,
          collapsed: false,
          color: 'text-green-400',
          icon: 'grok',
        },
      ]
    },
    userId, // Assign to current user
    files: {
      'rubric.moth': `[RUBRIC] v=1.0

[METADATA]
domain: code
created: ${new Date().toISOString()}

[CRITERIA]
- Clarity: 0.9
- Correctness: 1.0
- Performance: 0.8
- Maintainability: 0.9

[WEIGHTS]
clarity: 0.2
correctness: 0.4
performance: 0.2
maintainability: 0.2
`,
      'index.json': JSON.stringify({
        version: '1.0',
        runId: `run-${now}`,
        status: 'init',
        personas: []
      }, null, 2)
    },
    thread: {
      messages: [
        {
          id: 'system-1',
          role: 'system',
          content: `You are the Hivemind Orchestrator coordinating multi-LLM creative work.

CRITICAL: Respond in MOTH format ONLY. NO markdown. NO prose outside MOTH sections.

Your role:
1. Receive user requests and draft content
2. Coordinate peer reviews (Gemini, Claude, Grok)
3. Synthesize feedback and provide guidance
4. Help iterate on work until quality standards are met

Required MOTH structure for all responses:

[ORCHESTRATOR_RESPONSE]
packet_type:ORCHESTRATOR_RESPONSE
timestamp:${new Date().toISOString()}

[ANALYSIS]
request_type:<draft|revision|question|feedback>
complexity:<low|medium|high>
recommended_action:<what to do next>

[GUIDANCE]
<your analysis and recommendations in structured format>
key_points:
- <point 1>
- <point 2>

[NEXT_STEPS]
priority:high; action:<what user should do>
priority:medium; action:<optional improvement>

[STATUS]
ready_for_peers:<yes|no>
ready_for_judge:<yes|no>

NO markdown formatting. NO code blocks. MOTH format ONLY.

When user sends content, analyze it and provide structured guidance in MOTH format.`,
          timestamp: now,
        },
      ],
      peerResponses: {},
      judgeResult: null,
    }
  }
}

/**
 * Export project as JSON
 */
export function exportProject(project: Project): string {
  return JSON.stringify(project, null, 2)
}

/**
 * Import project from JSON
 */
export function importProject(json: string): Project {
  const project = JSON.parse(json) as Project
  project.id = `project-${Date.now()}` // Generate new ID
  project.updatedAt = Date.now()
  return project
}

/**
 * Save a directory handle for a project
 */
export async function saveDirectoryHandle(projectId: string, dirHandle: FileSystemDirectoryHandle): Promise<void> {
  const database = await initDB()

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([DIRECTORY_HANDLES_STORE], 'readwrite')
    const store = transaction.objectStore(DIRECTORY_HANDLES_STORE)

    const request = store.put(dirHandle, projectId)

    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
  })
}

/**
 * Get a directory handle for a project
 */
export async function getDirectoryHandle(projectId: string): Promise<FileSystemDirectoryHandle | null> {
  const database = await initDB()

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([DIRECTORY_HANDLES_STORE], 'readonly')
    const store = transaction.objectStore(DIRECTORY_HANDLES_STORE)

    const request = store.get(projectId)

    request.onsuccess = () => resolve(request.result || null)
    request.onerror = () => reject(request.error)
  })
}

/**
 * Save workspace directory handle
 */
export async function saveWorkspaceHandle(dirHandle: FileSystemDirectoryHandle): Promise<void> {
  const database = await initDB()

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([DIRECTORY_HANDLES_STORE], 'readwrite')
    const store = transaction.objectStore(DIRECTORY_HANDLES_STORE)

    const request = store.put(dirHandle, WORKSPACE_KEY)

    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
  })
}

/**
 * Get workspace directory handle
 */
export async function getWorkspaceHandle(): Promise<FileSystemDirectoryHandle | null> {
  const database = await initDB()

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([DIRECTORY_HANDLES_STORE], 'readonly')
    const store = transaction.objectStore(DIRECTORY_HANDLES_STORE)

    const request = store.get(WORKSPACE_KEY)

    request.onsuccess = () => resolve(request.result || null)
    request.onerror = () => reject(request.error)
  })
}

/**
 * Check if workspace is set up
 */
export async function hasWorkspace(): Promise<boolean> {
  try {
    const handle = await getWorkspaceHandle()
    if (!handle) return false

    // Verify we still have permission
    // @ts-ignore - File System Access API
    const permission = await handle.queryPermission({ mode: 'readwrite' })
    return permission === 'granted'
  } catch {
    return false
  }
}

/**
 * Request permission for workspace if needed
 */
export async function requestWorkspacePermission(): Promise<boolean> {
  try {
    const handle = await getWorkspaceHandle()
    if (!handle) return false

    // @ts-ignore - File System Access API
    const permission = await handle.requestPermission({ mode: 'readwrite' })
    return permission === 'granted'
  } catch {
    return false
  }
}

/**
 * Create or get .hivemind folder in project directory
 */
export async function getOrCreateHivemindFolder(
  projectDir: FileSystemDirectoryHandle
): Promise<FileSystemDirectoryHandle> {
  return await projectDir.getDirectoryHandle('.hivemind', { create: true })
}

/**
 * Save project to .hivemind/project.json
 */
export async function saveProjectToFileSystem(
  projectDir: FileSystemDirectoryHandle,
  project: Project
): Promise<void> {
  const hivemindDir = await getOrCreateHivemindFolder(projectDir)

  // Save project.json (without thread data to keep it light)
  const projectData = {
    id: project.id,
    name: project.name,
    description: project.description,
    createdAt: project.createdAt,
    updatedAt: project.updatedAt,
    settings: project.settings,
  }

  const projectFile = await hivemindDir.getFileHandle('project.json', { create: true })
  const writable = await projectFile.createWritable()
  await writable.write(JSON.stringify(projectData, null, 2))
  await writable.close()

  // Save thread.json separately (can be large)
  if (project.thread) {
    const threadFile = await hivemindDir.getFileHandle('thread.json', { create: true })
    const threadWritable = await threadFile.createWritable()
    await threadWritable.write(JSON.stringify(project.thread, null, 2))
    await threadWritable.close()
  }
}

/**
 * Load project from .hivemind/project.json
 */
export async function loadProjectFromFileSystem(
  projectDir: FileSystemDirectoryHandle
): Promise<Project | null> {
  try {
    const hivemindDir = await projectDir.getDirectoryHandle('.hivemind')

    // Load project.json
    const projectFile = await hivemindDir.getFileHandle('project.json')
    const projectFileData = await projectFile.getFile()
    const projectText = await projectFileData.text()
    const projectData = JSON.parse(projectText)

    // Load thread.json if it exists
    let thread = null
    try {
      const threadFile = await hivemindDir.getFileHandle('thread.json')
      const threadFileData = await threadFile.getFile()
      const threadText = await threadFileData.text()
      thread = JSON.parse(threadText)
    } catch {
      // Thread file doesn't exist yet, that's ok
    }

    // Load files from project directory (excluding .hivemind)
    const files: Record<string, string> = {}
    // @ts-ignore - File System Access API
    for await (const entry of projectDir.values()) {
      if (entry.kind === 'file' && entry.name !== '.DS_Store') {
        const file = await (entry as FileSystemFileHandle).getFile()
        const content = await file.text()
        files[entry.name] = content
      }
    }

    return {
      ...projectData,
      thread,
      files,
    }
  } catch (err) {
    console.error('Failed to load project from file system:', err)
    return null
  }
}

/**
 * List all projects in workspace
 */
export async function listWorkspaceProjects(): Promise<Array<{ name: string; handle: FileSystemDirectoryHandle }>> {
  const workspaceHandle = await getWorkspaceHandle()
  if (!workspaceHandle) return []

  const projects: Array<{ name: string; handle: FileSystemDirectoryHandle }> = []

  try {
    // @ts-ignore - File System Access API
    for await (const entry of workspaceHandle.values()) {
      if (entry.kind === 'directory') {
        // Check if it has a .hivemind folder
        try {
          const dirHandle = entry as FileSystemDirectoryHandle
          await dirHandle.getDirectoryHandle('.hivemind')
          projects.push({ name: entry.name, handle: dirHandle })
        } catch {
          // Not a hivemind project, skip
        }
      }
    }
  } catch (err) {
    console.error('Failed to list workspace projects:', err)
  }

  return projects
}

/**
 * Create a new project folder in workspace
 */
export async function createProjectInWorkspace(projectName: string): Promise<FileSystemDirectoryHandle | null> {
  const workspaceHandle = await getWorkspaceHandle()
  if (!workspaceHandle) return null

  try {
    // Create project folder
    const projectDir = await workspaceHandle.getDirectoryHandle(projectName, { create: true })

    // Create .hivemind folder
    await getOrCreateHivemindFolder(projectDir)

    return projectDir
  } catch (err) {
    console.error('Failed to create project in workspace:', err)
    return null
  }
}

